import { useEffect, useRef } from 'react'
import { Button } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'
import { useIsMiniContentHovered } from '../../atoms'
import type { SetupCallMoment } from '../schedule'
import { SetupCallRow, letGoClassName, tactileClassName, titleClassName } from './face'

/** Opened by the product and left alone, the invite settles back to the ears after this long. */
const SETTLE_MS = 8_000
/** The pointer leaving isn't a decision, so it never gets less than this before settling. */
const SETTLE_AFTER_HOVER_MS = 2_000

interface SetupCallInviteProps {
  moment: SetupCallMoment
  isPeek: boolean
  onPick: () => void
  onLater: () => void
  onNever: () => void
  onSettle: () => void
}

function useSettleWhenIgnored(isActive: boolean, onSettle: () => void) {
  const isHovered = useIsMiniContentHovered()
  const left = useRef(SETTLE_MS)
  useEffect(() => {
    if (!isActive || isHovered) return
    const startedAt = Date.now()
    const id = window.setTimeout(onSettle, left.current)
    return () => {
      window.clearTimeout(id)
      left.current = Math.max(SETTLE_AFTER_HOVER_MS, left.current - (Date.now() - startedAt))
    }
  }, [isActive, isHovered, onSettle])
}

export function SetupCallInvite({ moment, isPeek, onPick, onLater, onNever, onSettle }: SetupCallInviteProps) {
  useSettleWhenIgnored(isPeek, onSettle)

  return (
    <div className="flex flex-col" aria-labelledby="setup-call-invite-title" role="group">
      <SetupCallRow>
        <h2 id="setup-call-invite-title" className={twMerge(titleClassName, 'text-balance')}>
          Get Highlight set up for the way you work
        </h2>
      </SetupCallRow>
      <SetupCallRow className="mt-2">
        <p className="text-foreground text-md text-balance">
          Grab 30 minutes with a Highlight specialist. We’ll connect your tools, get you a real win from your own work,
          and show you exactly where Highlight fits into your week.
        </p>
      </SetupCallRow>
      <SetupCallRow className="mt-5 flex gap-2">
        <Button size="lg" className={tactileClassName} onClick={onPick}>
          Pick a time
        </Button>
        {/* Its second showing doesn't offer later again: the way out is for good */}
        <Button
          variant="ghost"
          tone="on-dark"
          size="lg"
          className={letGoClassName}
          onClick={moment === 'later' ? onNever : onLater}
        >
          {moment === 'later' ? 'Don’t show again' : 'Maybe later'}
        </Button>
      </SetupCallRow>
    </div>
  )
}
